import React, { useEffect, useState } from "react";
import "./WorkerSchedule.css";
import config from "../../config";
import { authFetch } from "../../utils/Security/authFetch";

// Calendar imports
import { Calendar, dateFnsLocalizer, Event } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import "react-big-calendar/lib/css/react-big-calendar.css";
import ApiDropdown from "../../Components/DropDown/Apidropdown";

const locales = {
  "en-US": require("date-fns/locale/en-US"),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface WorkerSchedule {
  id?: number;
  workerId: number;
  shiftType: string;
  startDatetime: string;
  endDatetime: string;
  status: string;
  customer: string;
}

const WorkerSchedulePage: React.FC = () => {
  const [schedules, setSchedules] = useState<WorkerSchedule[]>([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [formData, setFormData] = useState<WorkerSchedule>({
    workerId: Number(localStorage.getItem("userid")) || 0,
    shiftType: "",
    startDatetime: "",
    endDatetime: "",
    status: "on_call",
    customer: " ",
  });

  // Fetch schedules for logged-in user
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        const response = await authFetch(
          `${config.apiBaseUrl}/schedules/user/${formData.workerId}`,
          { method: "GET" }
        );
        if (response.ok) {
          const data = await response.json();
          setSchedules(data);
        } else {
          console.error("Failed to fetch schedules:", response.statusText);
        }
      } catch (error) {
        console.error("Error fetching schedules:", error);
      }
    };

    if (formData.workerId) fetchSchedules();
  }, [formData.workerId]);

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Add new schedule
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      workerId: formData.workerId,
      shiftStart: formData.startDatetime,
      shiftEnd: formData.endDatetime,
      status: formData.status,
      shiftType: formData.shiftType,
      customer: formData.customer,
    };

    try {
      const response = await authFetch(`${config.apiBaseUrl}/schedules`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const newSchedule = await response.json();
        setSchedules((prev) => [...prev, newSchedule]);
        setFormData({
          ...formData,
          shiftType: "",
          startDatetime: "",
          endDatetime: "",
        });
      } else {
        console.error("Failed to add schedule:", response.statusText);
      }
    } catch (error) {
      console.error("Error adding schedule:", error);
    }
  };

  // Delete a schedule
  const handleDelete = async (id?: number) => {
    if (!id) return;
    if (!window.confirm("Are you sure you want to delete this schedule?"))
      return;

    try {
      const response = await authFetch(`${config.apiBaseUrl}/schedules/${id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setSchedules((prev) => prev.filter((s) => s.id !== id));
      } else {
        console.error("Failed to delete schedule:", response.statusText);
      }
    } catch (error) {
      console.error("Error deleting schedule:", error);
    }
  };

  // Convert schedules to calendar events
  const events: Event[] = schedules.map((s) => ({
    id: s.id,
    title: `${s.shiftType} (${s.status})`,
    start: new Date(s.startDatetime),
    end: new Date(s.endDatetime),
    status: s.status,
  }));

  // Event style based on status
  const eventStyleGetter = (event: any) => {
    let backgroundColor = "#3174ad"; // default blue
    if (event.status === "on_call") backgroundColor = "#f59e0b"; // orange
    if (event.status === "available") backgroundColor = "#10b981"; // green
    if (event.status === "off") backgroundColor = "#ef4444"; // red

    return {
      style: {
        backgroundColor,
        color: "white",
        borderRadius: "4px",
        border: "none",
        padding: "2px",
      },
    };
  };
  const handleCustomerChange = (val: string | number | (string | number)[]) => {
    setFormData((prev) => ({
      ...prev,
      customer: Array.isArray(val) ? val.join(",") : String(val),
    }));
  };

  return (
    <div className="schedule-container min-h-screen flex flex-col items-center py-8 px-4">
      <h1 className="schedule-title text-3xl mb-6">
        Worker Schedule Management
      </h1>

      {/* Toggle Calendar Button */}
      <button
        onClick={() => setShowCalendar((prev) => !prev)}
        className="mb-6 px-4 py-2 bg-blue-600 text-white rounded"
      >
        {showCalendar ? "Hide Calendar" : "Show Calendar"}
      </button>

      {/* Calendar */}
      {showCalendar && (
        <div className="w-full max-w-5xl mb-8">
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 500 }}
            eventPropGetter={eventStyleGetter}
          />
        </div>
      )}

      {/* Add Schedule Form */}
      <form
        onSubmit={handleAdd}
        className="schedule-form w-full max-w-xl bg-white shadow-md rounded-lg p-6 space-y-4"
      >
        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <ApiDropdown
            api="affected-customers"
            labelKey="customername"
            required={true}
            valueKey="customername"
            value={formData.customer}
            label="Customer"
            isMulti
            onChange={handleCustomerChange}
          />

          <input
            type="text"
            name="shiftType"
            value={formData.shiftType}
            onChange={handleChange}
            placeholder="e.g. Morning, Evening, Night"
            required
          />
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Shift Start
            </label>
            <input
              type="datetime-local"
              name="startDatetime"
              value={formData.startDatetime}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Shift End</label>
            <input
              type="datetime-local"
              name="endDatetime"
              value={formData.endDatetime}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
          >
            <option value="on_call">On Call</option>
            <option value="off">Off</option>
            <option value="available">Available</option>
          </select>
        </div>

        <button type="submit" className="w-full add-btn">
          Add Schedule
        </button>
      </form>

      {/* Schedule Table */}
      <div className="w-full max-w-5xl mt-10 overflow-x-auto">
        <table className="schedule-table w-full border-collapse">
          <thead>
            <tr>
              <th>ID</th>
              <th>Shift Type</th>
              <th>Shift Start</th>
              <th>Shift End</th>
              <th>Status</th>
               <th>Customer</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {schedules.length > 0 ? (
              schedules.map((schedule) => (
                <tr key={schedule.id}>
                  <td>{schedule.id}</td>
                  <td>{schedule.shiftType}</td>
                  <td>{new Date(schedule.startDatetime).toLocaleString()}</td>
                  <td>{new Date(schedule.endDatetime).toLocaleString()}</td>
                  <td>
                    <span className={`status-badge status-${schedule.status}`}>
                      {schedule.status}
                    </span>
                  </td>
                      <td>{schedule.customer}</td>
                  <td>
                    <button
                      onClick={() => handleDelete(schedule.id)}
                      className="delete-btn"
                      title="Delete schedule"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center py-4 text-gray-500">
                  No schedules found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkerSchedulePage;
